import { Player, PlaylistEntry } from '../player';
import { ComscoreConfig } from './comscoreConfig';
import { ChapterEvent } from './../player';

declare const ns_: any;

interface UpdateClipOptions {
    isAd: boolean;
    ad?: AdImpressionOptions;
    looped?: boolean;
}
class StreamsenseContentType {
    public static SHORT_FORM_VOD: string = 'vc11';
    public static LONG_FORM_VOD: string = 'vc12';
    public static LIVE: string = 'vc13';
    public static PREROLL_AD: string = 'va11';
    public static MIDROLL_AD: string = 'va12';
    public static LIVE_AD: string = 'va21';
}

const enum StreamsenseVideoType {
    BROADCAST_TV = 4,
    WEBISODE = 3
}

const { StreamingAnalytics } = ns_;
const { ContentMetadata } = StreamingAnalytics;
const AdMetadata = StreamingAnalytics.AdvertisementMetadata;
const { PlayerEvents } = StreamingAnalytics;

export class StreamsenseManager {

    private _currentEvent: number | string;
    private _videoStarted: boolean = false;
    private _videoSeeking: boolean = false;
    private _wasAd: boolean = false;
    private _clipNumber: number = 0;
    private _contentClipNumber: number = 0;
    private _videoType: StreamsenseVideoType;
    private _videoContentType: string;
    private _videoDuration: number;
    private _videoId: string;
    private _videoFormattedDate: string;
    private _videoFormattedAvailableDate: string;
    private _videoSeasonNumber: string;
    private _videoEpisodeNumber: string;
    private _videoGenre: string;
    private _videoItem: PlaylistEntry;
    private _trackingTitle: string;
    private _adStartTime: number = 0;
    private _adElapsedTime: number = 0;
    private _currentAd: any;
    private _currentClipId: string | null;
    private _missingValue: string = '*null';
    private _wasPlayingBeforeSeek: boolean = false;

    // SA instance methods are own-properties (i.extend pattern), not on prototype
    public streamSense: any;

    constructor(
        private _config: ComscoreConfig,
        private _player: Player,
        private _secured: boolean = false
    ) {
        this._config.c1 = this._config.c1 || '2';
        this._config.c4 = this._config.c4 || this._missingValue;
        this._config.c2 = this._config.c2 || '3005670';
        this._config.channel = this._config.channel || this._config.c3;
        this._config.consent = this._config.consent || '1';
        const pub = new ns_.configuration.PublisherConfiguration({
            publisherId: this._config.c2
        });
        ns_.configuration.addClient(pub);
        ns_.configuration.disableCmpIntegration();
        ns_.configuration.disableTcfIntegration();
        ns_.configuration.addPersistentLabels({ cs_ucfr: this._config.consent });
        ns_.start();
        ns_.notifyEnterForeground();
        ns_.notifyUxActive();

        this.streamSense = new StreamingAnalytics();
    }

    private _formatEpisodeSeason(prefix: string, value: string = null): string {
        let result: string = value || '';
        if (result) {
            result = prefix + this._pad(result);
        }
        return result;
    }

    private _pad(s: string) {
        return s.length < 2 ? '0' + s : s;
    }

    private _formatDate(d: any) {
        return d.getUTCFullYear() + '-'
            + this._pad(d.getUTCMonth() + 1) + '-'
            + this._pad(d.getUTCDate());
    }

    private _generateTrackingTitle(item: PlaylistEntry): string {
        let result = item.show ? item.show.replace(/[\s]+/g, '') : '';
        const e = this._formatEpisodeSeason('E', item.metadata.episodeNumber);
        const s = this._formatEpisodeSeason('S', item.metadata.seasonNumber);
        if (s) result += '-' + s;
        if (e) result += '-' + e;
        if (!e) result += (result ? '-' : '') + item.title.replace(/[\s]+/g, '');
        return result;
    }

    private _log(message: string, info?: any) {
        this._player.log('[comscore streamsense]', message, info);
    }

    private _getTimestamp(): number {
        return (new Date()).getTime();
    }

    private _toMS(s: number): number {
        return Math.round(s * 1000);
    }

    private _start() {
        this._log('_start()');
        this.streamSense.createPlaybackSession();
    }

    private _resolveContentType(): string {
        if (this._videoItem.metadata.liveStream) {
            return ContentMetadata.ContentType.LIVE;                  // "13"
        }
        if (this._videoContentType === StreamsenseContentType.LONG_FORM_VOD) {
            return ContentMetadata.ContentType.LONG_FORM_ON_DEMAND;   // "12"
        }
        return ContentMetadata.ContentType.SHORT_FORM_ON_DEMAND;      // "11"
    }

    //  Build ContentMetadata for Comscore V7.14

    private _buildContentMetadata(): any {
        const meta = new ContentMetadata();
        meta.setUniqueId(
            this._videoItem.metadata.ns_st_ci || this._videoId
        );
        meta.setProgramTitle(this._videoItem.show || this._missingValue);
        meta.setEpisodeTitle(this._trackingTitle);
        meta.setPublisherName(this._config.c3);
        meta.setLength(this._videoDuration);
        meta.setTotalSegments(this._player.totalChapters);
        meta.setMediaType(this._resolveContentType());
        meta.setEpisodeSeasonNumber(this._videoSeasonNumber);
        meta.setEpisodeNumber(this._videoEpisodeNumber);
        meta.setGenreName(this._videoGenre);
        meta.setStationTitle(this._config.channel);
        meta.setStationCode(this._config.VAM_station_code || this._missingValue);
        meta.setProgramId(this._videoId || this._missingValue);
        meta.setEpisodeId(this._videoItem.metadata.ns_st_tep || this._videoEpisodeNumber);

        meta.classifyAsCompleteEpisode(this._videoItem.metadata.clipType === 'episode');

        // Dictionary classifications
        meta.setDictionaryClassificationC3(this._config.c3);
        meta.setDictionaryClassificationC4(this._config.c4);
        meta.setDictionaryClassificationC6(this._trackingTitle);

        // Delivery metadata — only when present
        if (this._videoItem.metadata.ns_st_cmt
            && this._videoItem.metadata.ns_st_cmt !== this._missingValue) {
            meta.setMediaFormat(this._videoItem.metadata.ns_st_cmt);
        }
        if (this._videoItem.metadata.ns_st_cdm
            && this._videoItem.metadata.ns_st_cdm !== this._missingValue) {
            meta.setDistributionModel(this._videoItem.metadata.ns_st_cdm);
        }
        // Fields with no dedicated setter merged via addCustomLabels
        meta.addCustomLabels({
            c16: '1',
            c9: this._missingValue,
            ns_st_de: this._videoItem.show || this._config.c3,
            ns_st_ti: this._missingValue,
            ns_st_ddt: this._videoFormattedAvailableDate,
            ns_st_tdt: this._videoItem.metadata.ns_st_tdt || this._missingValue,
            ns_st_tm: this._videoItem.metadata.ns_st_tm || this._missingValue,
        });

        // Track clip ID for the live-stream re-init guard in trackPlay()
        this._currentClipId = this._videoItem.metadata.ns_st_ci || this._videoId;

        return meta;
    }

    //  Build AdvertisementMetadata
    /* AdvertisementType = { 
        ON_DEMAND_PRE_ROLL:"11", 
        ON_DEMAND_MID_ROLL:"12",
        ON_DEMAND_POST_ROLL:"13", 
        LIVE:"21", 
        BRANDED_ON_DEMAND_PRE_ROLL:"31"}
    */

    private _buildAdMetadata(options: UpdateClipOptions): any {
        let adLength = 0;
        let adId = '';
        let adType = AdMetadata.AdvertisementType.OTHER;

        if (options?.ad?.ima && options?.ad?.ima?.ad) {
            adLength = options.ad.ima.ad.getDuration();
            adId = options.ad.ima.ad.getAdId();
            const podIndex = options.ad.ima.ad.getAdPodInfo().getPodIndex();

            if (this._videoItem.metadata.liveStream) {
                adType = AdMetadata.AdvertisementType.LIVE;                // "21"
            } else {
                adType = podIndex === 0
                    ? AdMetadata.AdvertisementType.ON_DEMAND_PRE_ROLL      // "11"
                    : AdMetadata.AdvertisementType.ON_DEMAND_MID_ROLL;     // "12"
            }
        } else if ('dai' === options?.ad?.client) {
            adId = options.ad.id;
            adLength = options.ad.duration;
            adType = AdMetadata.AdvertisementType.LIVE;                  // "21"
        }

        const adMeta = new AdMetadata();
        adMeta.setLength(adLength > 0 ? this._toMS(adLength) : 0);
        adMeta.setMediaType(adType);
        adMeta.addCustomLabels({
            ns_st_ci:  this._videoId,
        });

        // Non-VAM mode: override ns_st_ad to "1"
        if (!this._config.VAM_enabled) {
            adMeta.addCustomLabels({ ns_st_ad: '1' });
        }

        return adMeta;
    }

    private _updateClip(options: UpdateClipOptions) {
        if (options.isAd || !this._contentClipNumber) {
            this._clipNumber++;
        }
        if (this._videoFormattedDate === '1970-1-1') {
            this._videoFormattedDate = this._missingValue;
        }
        if (this._videoFormattedAvailableDate === '1970-1-1') {
            this._videoFormattedAvailableDate = this._missingValue;
        }

        if (options.isAd) {
            this.streamSense.setMetadata(this._buildAdMetadata(options));
        } else {
            this._contentClipNumber = this._contentClipNumber || this._clipNumber;
            this.streamSense.setMetadata(this._buildContentMetadata());
        }

        this._wasAd = options.isAd;
        this._log('updateClip | ' + (options.isAd ? '*AD*' : '*CONTENT*'));
    }

    public setup() {
        this._log('setup', this._player.currentMedia);
        this._videoItem = this._player.currentMedia;
        this._trackingTitle = this._generateTrackingTitle(this._videoItem);
        this._videoType = this._videoItem.metadata.clipType === 'webisode'
            ? StreamsenseVideoType.WEBISODE
            : StreamsenseVideoType.BROADCAST_TV;
        this._videoDuration = this._toMS(this._videoItem.metadata.duration);
        this._videoContentType = this._videoItem.metadata.liveStream
            ? StreamsenseContentType.LIVE
            : (this._videoItem.metadata.clipType === 'episode'
                ? StreamsenseContentType.LONG_FORM_VOD
                : StreamsenseContentType.SHORT_FORM_VOD);
        this._videoId = this._videoItem.mediaId;
        this._videoSeasonNumber = this._videoItem.metadata.seasonNumber
            ? this._videoItem.metadata.seasonNumber.replace(/^0+/, '')
            : this._missingValue;
        this._videoEpisodeNumber = this._videoItem.metadata.episodeNumber
            ? this._videoItem.metadata.episodeNumber.replace(/^0+/, '')
            : this._missingValue;
        this._videoGenre = this._videoItem.metadata.genre || this._missingValue;
        this._videoFormattedDate = this._formatDate(
            this._videoItem.metadata.liveStream
                ? new Date()
                : new Date(this._videoItem.metadata.airDate)
        );

        const availableDate = this._videoItem.metadata.availableDate
            ? new Date(this._videoItem.metadata.availableDate)
            : null;
        const availabilityWindow = this._videoItem.metadata.availabilityWindow
            ? new Date(
                this._videoItem.metadata.availabilityWindow
                    .split(';')[0]
                    .replace('start=', '')
            )
            : new Date();
        this._videoFormattedAvailableDate = this._formatDate(
            availableDate ? availableDate : availabilityWindow
        );

        this._start();
    }

    public trackAdStart(e: AdImpressionOptions) {
        this._log('trackAdStart()');
        this._adStartTime = this._getTimestamp();
        this._adElapsedTime = 0;

        if (this._videoStarted && this._currentEvent !== PlayerEvents.END) {
            if (!this._wasAd) {
                this._notifyEnd(this._toMS(this._player.currentMediaPosition));
            } else if (this._currentAd) {
                this._notifyEnd(this._toMS(this._getAdDuration()));
            }
        }

        this._currentAd = e.ima && e.ima.ad ? e.ima.ad : e;
        this._videoStarted = true;
        this._updateClip({ isAd: true, ad: e });
    }

    public trackAdPause(e: AdPauseResumeOptions) {
        this._log('trackAdPause()');
        this._adElapsedTime = this._getTimestamp() - this._adStartTime;
        this._notifyPause(this._adElapsedTime);
    }

    public trackAdResume(e: AdPauseResumeOptions) {
        this._log('trackAdResume()');
        let elapsedTime = 0;
        if (this._currentEvent === PlayerEvents.PAUSE) {
            elapsedTime = this._adElapsedTime;
            this._adStartTime = this._getTimestamp() - elapsedTime;
        }
        this._notifyPlay(elapsedTime);
    }

    public trackAdComplete(e: AdCompleteOptions) {
        this._log('trackAdComplete()');
        this._notifyEnd(this._getAdDuration());
    }

    public trackComplete() {
        this._log('trackComplete()');
        this._notifyEnd(this._toMS(this._player.currentMediaPosition));
    }

    public trackBuffering() {
        if (this._videoStarted && !this._videoSeeking) {
            this._notifyBuffer(this._toMS(this._player.currentMediaPosition));
        }
    }

    public trackSeeking(e: SeekEvent) {
        this._log('trackSeeking()', e);
        this._videoSeeking = true;
        if (this._videoStarted) {
            this._wasPlayingBeforeSeek = this._currentEvent === PlayerEvents.PLAY;
            this._currentEvent = PlayerEvents.SEEK_START;  // 11
            this.streamSense.notifySeekStart();
            this._log('_notifySeekStart() Notify SEEK_START');
        }
    }

    public trackSeeked() {
        if (this._videoStarted) {
            this._log('trackSeeked()');
            this._videoSeeking = false;
            if (this._wasPlayingBeforeSeek) {
                this._notifyPlay(this._toMS(this._player.currentMediaPosition));
            } else {
                this._notifyPause(this._toMS(this._player.currentMediaPosition));
            }
        }
    }

    public trackChapterStart(e: ChapterEvent) {
        this._log('trackChapterStart()', e);
        const time = this._toMS(this._player.currentMediaPosition);

        if (this._videoStarted && this._currentEvent !== PlayerEvents.END) {
            this._notifyEnd(time);
        }

        this._videoStarted = true;
        this._updateClip({ isAd: false });
        this.streamSense.startFromSegment(this._player.currentChapter);

        this._notifyPlay(time);
    }

    public trackPlay() {
        this._videoSeeking = false;

        if (this._wasAd) {
            this._log('trackPlay() after ad break');
            if (this._currentEvent !== PlayerEvents.END) {
                this._notifyEnd(this._toMS(this._getAdDuration()));
            }
            this._updateClip({ isAd: false });
            this._currentAd = null;
        } else {
            if (this._player.currentMedia.metadata.liveStream
                && (!this._currentClipId || this._currentClipId !== this._videoId)) {
                this._updateClip({ isAd: false });
            }
            this._log('trackPlay() resume paused content');
        }

        if (this._currentEvent !== PlayerEvents.PLAY) {
            this._notifyPlay(this._toMS(this._player.currentMediaPosition));
        }
    }

    public trackPause() {
        if (!this._videoSeeking) {
            this._log('trackPause()');
            this._notifyPause(this._toMS(this._player.currentMediaPosition));
        }
    }

    public _notifyBuffer(timeInMS: number) {
        this._currentEvent = PlayerEvents.BUFFER;          // 4
        this.streamSense.notifyBufferStart();
        this._log('_notifyBuffer() Notify BUFFER', timeInMS);
    }

    public _notifyPause(timeInMS: number) {
        this._currentEvent = PlayerEvents.PAUSE;           // 1
        this.streamSense.notifyPause();
        this._log('_notifyPause() Notify PAUSE | ' + new Date(), timeInMS);
    }

    private _notifyPlay(timeInMS: number) {
        this._currentEvent = PlayerEvents.PLAY;            // 0
        this.streamSense.notifyPlay();
        this._log('_notifyPlay() Notify PLAY | ' + new Date(), timeInMS);
    }

    private _notifyEnd(timeInMS: number) {
        this._currentEvent = PlayerEvents.END;             // 3
        this.streamSense.notifyEnd();
        this._log('_notifyEnd() Notify END', timeInMS);
    }

    private _getAdDuration(): number {
        if (this._currentAd) {
            return 'dai' === this._currentAd.client
                ? this._currentAd.duration
                : this._currentAd.getDuration();
        }
        return 0;
    }

    public reset() {
        this._log('reset()');
        this._videoStarted = false;
        this._currentEvent = '';
        this._clipNumber = 0;
        this._contentClipNumber = 0;
        this._currentClipId = null;
        this._wasPlayingBeforeSeek = false;
    }
}
